<template>
    <div class="relative w-full">
        <input
            id="input-field"
            ref="inputField"
            class="input-suggest-field lg-max:!py-6 !pr-8 !truncate text-hh-grey-900 !shadow-none offset-top"
            v-model="query"
            @paste="onInput"
            @keydown="onKeydown"
            @click="onFocusSelect"
            @blur="handleInputBlur"
            :placeholder="placeholder"
            type="text"
            aria-autocomplete="list"
            aria-controls="suggestions-list"
            :aria-activedescendant="selectedOption"
            autocomplete="off"
        />

        <div
            class="ml-4 flex items-center absolute -bottom-4 text-sm text-hh-orange font-normal whitespace-nowrap"
            :class="{ 'lg-max:bg-hh-grey-50' : this.context !== 'homepage' }"
            v-if="showWarning"
        >
            <img class="mr-2 w-4 h-4" src="/assets/img/frontend-v3/icons/global/icon-info-orange.svg" alt="info">
            <template v-if="context === 'homepage'">
                {{ noResultsTextShort }}
            </template>
            <template v-else>
                {{ noResultsText }}
            </template>
        </div>

        <div
            v-if="query.length > 0 || this.filters.length"
            class="absolute lg-max:right-1 lg:right-0 top-2.5 text-2xl text-hh-grey-500 w-6 text-center cursor-pointer transition"
            @click="clearAllFilters"
        >×</div>

        <template v-if="allowFilters">
            <input type="hidden"
                   v-for="filter in filters"
                   :data-filter-reference="filter.category"
                   :data-selected-value="filter.reference"
            >

            <!-- Coordinates / destination filter data  -->
            <input type="hidden"
                   :data-filter-reference="filterCategory"
                   :data-selected-value="filterReference"
            >
        </template>

        <!-- Results dropdown -->
        <ul
            v-if="this.query.length > 0 && (this.filteredResults.length > 0 || this.isLoading)"
            id="suggestions-list"
            class="results-list"
            :class="{ 'inactive': ! this.isOpen  }"
            role="listbox"
            aria-labelledby="input-field"
            aria-live="polite"
            @mousedown.prevent="() => {}"
        >
            <li
                class="loading"
                id="option-1"
                v-if="this.isLoading"
            ><div class="loader"></div></li>

            <li
                v-if="filteredResults.length > 0"
                v-for="(option, index) in filteredResults"
                :id="'option' + index"
                :key="option._id"
                :ref="el => setSuggestionRef(index, el)"
                class="input-suggest-result"
                :class="{ 'selected': index === selectedIndex, 'no-result': option.source === 'X', [option.type]: true }"
                @click.prevent="selectOption(option)"
                :aria-selected="index === selectedIndex ? 'true' : 'false'"
                role="option"
            >
                <a :href="option.url" v-if="option.url" target="_blank">
                    <span>{{ option.name }}</span>
                    <small>{{ option.subtitle ?? '' }}{{ option.source ? ' (' + option.source + ')' : ' ' }}</small>
                </a>
                <template v-else>
                    <span>{{ option.name }}</span>
                    <small>{{ option.subtitle ?? '' }}{{ option.source ? ' (' + option.source + ')' : ' ' }}</small>
                </template>
            </li>
        </ul>
    </div>
</template>

<script>

export default {

    name: "InputSuggest",

    props: ['filter-data', 'enable-filters', 'context'],

    data() {
        return {
            allowFilters: true,
            isOpen: true,
            isLoading: false,
            showWarning: false,
            minChars: 2,
            typingTimeout: null,
            query: '',
            previousQuery: '',
            filteredResults: [],
            suggestionRefs: [],
            selectedIndex: -1,

            filters: [],
            filterCategory: '',
            filterReference: '',

            controller: null,

            selected: {
                'country': [],
                'province': [],
                'region': [],
                'place': [],
            },

            hasUpdatedFilters: false,
        };
    },

    created() {

        this.query = this.getCurrentQuery();
        this.previousQuery = this.query;

        //because we're showing 2 instances of this component on one page, it's more convenient to allow filters
        //and the triggering of filters on only one of these components, else they will mess with each other
        this.allowFilters = this.enableFilters ?? this.allowFilters;

        if (this.filterData['geo-location']?.type && this.filterData['geo-location']?.id) {
            this.filterCategory = this.filterData['geo-location']?.reference;
            this.filterReference = this.filterData['geo-location']?.id;
        }

        this.$nextTick(() => {
            this.initSelectedFilters();
        });
    },

    mounted() {
        if (this.query === '') {
            // this.$refs.inputField.focus();
        }

        if (this.query.length > this.minChars && this.isOnMobile) {
            this.Bus.emit('close-destination-sub-filters');
        }

        document.addEventListener('click', this.closeOnClickOutside);

        this.Bus.on('clear-input-suggest', () => {
            this.clearFilters();

            this.$nextTick(() => {
                this.$refs.inputField.focus();
                this.$refs.inputField.select();
            })
        });

        this.Bus.on('suggest-filter-change', (cancels) => {
            if (this.allowFilters) {
                this.$nextTick(() => {
                    this.Bus.emit('filter-change', cancels ?? '');
                })
            }
        });

        this.Bus.on('set-suggest-filters', (filters) => {
            this.filters = filters;
        });

        this.Bus.on('clear-filter', (data) => {

            if (! this.allowFilters) {
                return;
            }

            if (data.baseCategory === 'coordinates' || this.selected.hasOwnProperty(data.baseCategory)) {

                this.query = '';
                this.previousQuery = '';
                this.clearFilterData();

                if (this.selected.hasOwnProperty(data.baseCategory)) {

                    this.selected[data.baseCategory] = [];

                    //replace query with "parent" in a very hacky way
                    if (data.baseCategory === 'province' || data.baseCategory === 'region') {
                        let parent = document.querySelector('div[data-filter-category="country"]');
                        this.query = parent.innerHTML;
                    } else if (data.baseCategory === 'place') {
                        let parent = document.querySelector('div[data-filter-category="province"]');
                        this.query = parent.innerHTML;
                    }

                } else {
                    this.clearSelected();
                }
            } else if (data.baseCategory === this.filterCategory && data.reference === this.filterReference) {
                this.query = '';
                this.filterCategory = '';
                this.filterReference = '';
            }
        });

        this.Bus.on('clear-all-filters', (filters) => {

            if (this.allowFilters) {

                this.query = '';
                this.previousQuery = '';
                this.clearFilterData();
                this.clearSelected();
            }
        });

        this.Bus.on('activate-filter', (reference) => {
            if (reference === 'destination' && this.query.length === 0) {
                this.$refs.inputField.focus();

                if (this.isOnMobile) {
                    this.Bus.emit('open-destination-sub-filters', 'destination')
                }
            }
        });

        this.Bus.on('filter-change', (reference) => {
            this.showWarning = false;
        });

        this.Bus.on('filter-deselect', (reference) => {
            if (reference === this.filterReference) {
                this.query = '';
                this.filterCategory = '';
                this.filterReference = '';

                this.filters = [];
            }
        });

        this.$refs.inputField.addEventListener('input', this.handleInputCapture, true);
    },

    beforeUnmount() {
        // Crucially, cancel any ongoing request when the component is destroyed
        if (this.controller) {
            this.controller.abort();
        }
    },

    watch: {
        filterData: function(newFilters, oldFilters) {

            let targetFilters = null;

            if (newFilters['geo-location']?.isSelected) {
                targetFilters = newFilters['geo-location'];

                this.filterCategory = targetFilters.reference;
                this.filterReference = targetFilters.id;

            } else if (newFilters.place?.isSelected) {
                targetFilters = newFilters.place;
            } else if (newFilters['province-region']?.isSelected) {
                targetFilters = newFilters['province-region'];
            } else if (newFilters.country?.isSelected) {
                targetFilters = newFilters.country;
            }

            if (targetFilters !== null) {
                this.query = targetFilters.title;
            } else {
                this.filterCategory = '';
                this.filterReference = '';
            }
        },
    },

    methods: {

        getCurrentQuery() {

            if (this.filterData['geo-location']?.items[0]?.title) {
                return this.filterData['geo-location']?.items[0]?.title;
            } else if (this.filterData.place?.isSelected) {
                return this.filterData.place?.title;
            } else if (this.filterData['province-region']?.isSelected) {
                return this.filterData['province-region']?.title;
            } else if (this.filterData.country?.isSelected) {
                return this.filterData.country?.title;
            }

            return '';
        },

        // Dynamically assign refs to suggestions
        setSuggestionRef(index, el) {
            this.suggestionRefs[index] = el;
        },

        beforeDestroy() {
            document.removeEventListener('click', this.closeOnClickOutside);
            this.$refs.inputField.removeEventListener('input', this.handleInputCapture, true);
        },

        handleInputCapture(event){
            this.query = event.target.value;
            this.onInput();
        },

        // Scroll to the selected item
        scrollToSelected() {
            const activeElement = this.suggestionRefs[this.selectedIndex];
            if (activeElement) {
                activeElement.scrollIntoView({block: 'nearest', behavior: 'smooth'});
            }
        },

        // Handles input event and triggers a debounced API call
        onInput() {

            if (! this.isOnMobile) {
                this.Bus.emit('deactivate-filter-external', 'destination');
            } else {
                this.Bus.emit('close-destination-sub-filters');
            }

            this.showWarning = false;

            //this is triggered when the user backspaces or deletes the input query
            if (this.query.length <= this.minChars && this.query.length < this.previousQuery.length) {

                this.previousQuery = this.query;

                if (this.query.length >= 0 && this.hasUpdatedFilters) {

                    this.clearFilterData();
                    this.Bus.emit('set-suggest-filters', []);

                    this.$nextTick(() => {
                        //also remove distance
                        this.Bus.emit('suggest-filter-change', this.filterData['geo-location']?.translations['distanceFilter']);
                    })
                }

                this.filteredResults = [];
                this.isOpen = false;

                this.toggleFilters(false);

                if (this.controller) {
                    this.controller.abort();
                    this.controller = null;
                }

                return;
            }

            clearTimeout(this.typingTimeout);

            if (this.query !== this.previousQuery && this.query.length >= this.minChars) {

                if (this.controller) {
                    this.controller.abort();
                }

                this.typingTimeout = setTimeout(() => {
                    this.fetchSuggestions();
                }, 500);
            }

        },

        // Fetch suggestions from the API based on the user's query
        async fetchSuggestions() {

            this.isLoading = true;

            const rawQuery = this.query

            this.filteredResults = [];

            if (rawQuery.trim() === '') {
                this.isLoading = false;
                return;
            }

            this.previousQuery = rawQuery;

            this.controller = new AbortController();
            const { signal } = this.controller;

            const params = new URLSearchParams({ q: rawQuery });
            const url = '/search-input?' + params.toString();

            try {
                const response = await fetch(url, { signal });
                this.filteredResults = await response.json();

                if (this.filteredResults.length > 0) {
                    this.open();

                    if (! this.isOnMobile) {
                        this.Bus.emit('deactivate-filter-external', 'destination');
                    }
                } else {
                    this.showWarning = true;
                    this.close();
                    if (! this.isOnMobile) {
                        this.Bus.emit('activate-filter-external', 'destination');
                    } else {
                        this.Bus.emit('activate-destination');
                    }
                }

                this.suggestionRefs = [];

                this.isLoading = false;

            } catch (error) {
                // Check if the error is due to an abort and handle it gracefully
                if (error.name === 'AbortError') {
                    // console.log('Previous request was cancelled.');
                } else {
                    console.error('Error fetching suggestions:', error);
                }
            } finally {
                this.isLoading = false;
                // Clear the controller when the request is done (or aborted)
                this.controller = null;
            }
        },

        onKeydown(event) {

            if (event.key === 'ArrowDown') {
                if (this.selectedIndex < this.filteredResults.length - 1) {
                    this.selectedIndex++;
                } else {
                    this.selectedIndex = 0; // Wrap around to the first item
                }

                this.scrollToSelected();

            } else if (event.key === 'ArrowUp') {
                if (this.selectedIndex > 0) {
                    this.selectedIndex--;
                } else {
                    this.selectedIndex = this.filteredResults.length - 1; // Wrap around to the last item
                }

                this.$refs.inputField.focus();
                this.$refs.inputField.setSelectionRange(99, 99);

                this.scrollToSelected();

            } else if (event.key === 'Enter') {

                event.preventDefault();

                if (this.selectedIndex !== -1) {
                    this.selectOption(this.filteredResults[this.selectedIndex]);
                } else if (this.query.trim() !== '') {
                    this.handleQueryEnter();
                }
            }
        },

        handleInputBlur() {
            // Clear any previous blur timeout to avoid conflicts
            if (this.blurTimeout) {
                clearTimeout(this.blurTimeout);
            }
            // Set a timeout to hide the suggestions after a brief delay.
            // This allows the click event on a suggestion to complete.
            this.blurTimeout = setTimeout(() => {
                this.isOpen = false; // Or this.showSuggestions = false; depending on your setup
                this.selectedIndex = -1; // Reset selection
            }, 150); // A 100-200ms delay is usually good for this.
        },

        selectOption(option) {

            // Clear the blur timeout immediately when an option is selected.
            // This prevents the list from hiding after the delay if it was clicked.
            if (this.blurTimeout) {
                clearTimeout(this.blurTimeout);
                this.blurTimeout = null;
            }

            if (this.$refs.inputField) {
                this.$refs.inputField.blur();
            }

            setTimeout(() => {

                this.$refs.inputField.blur();

                this.filteredResults = [];
                this.selectedIndex = -1;

                this.close();

                if (option.url) {
                    window.open(option.url, '_blank');
                    return;
                }

                this.query = option.name;
                this.hasUpdatedFilters = true;

                this.clearSelected();
                this.clearFilterData();

                this.Bus.emit('set-suggest-filters', option.filter);

                this.$nextTick(() => {
                    this.Bus.emit('check-and-activate-filter-external', 'arrival')
                    this.Bus.emit('suggest-filter-change');
                })

            }, 50); // Delay in milliseconds (e.g., 100mss)
        },

        handleQueryEnter() {

            const existsInResults = this.filteredResults.some(option => option.name === this.query);
            const hasAnyResults = this.filteredResults.length > 0;

            if (existsInResults) {
                const matchedOption = this.filteredResults.find(option => option.name === this.query);
                this.selectOption(matchedOption);
            } else if (hasAnyResults) {
                this.selectOption(this.filteredResults[0]);
            }
        },

        // Select all text in the input when it gains focus
        onFocusSelect() {

            var alreadyOpen = false;

            // this.$refs.inputField.select();
            this.close();
            this.showWarning = false;

            if (this.filteredResults.length > 0) {
                this.isOpen = true;
            } else {
                alreadyOpen = true;
                this.Bus.emit('activate-filter-external', 'destination')
            }

            this.toggleFilters(alreadyOpen);

            if (this.query.length >= this.minChars && this.filteredResults.length === 0) {
                // this.fetchSuggestions();
            }
        },

        toggleFilters(alreadyOpen) {

            if (this.query.length <= this.minChars) {
                if (! this.isOnMobile) {
                    if (! alreadyOpen) {
                        this.Bus.emit('activate-filter-external', 'destination')
                    }
                } else {
                    this.Bus.emit('open-destination-sub-filters', 'destination')
                    this.showWarning = false;
                }
            } else if (this.query.length >= this.minChars) {
                if (this.isOnMobile) {
                    this.Bus.emit('close-destination-sub-filters');
                }  else {
                    this.Bus.emit('close-filters');
                    this.showWarning = false;
                }
            }
        },

        closeOnClickOutside(event) {

            const classList = event.target.classList;

            if (
                classList.contains('input-suggest')
                || classList.contains('input-suggest-field')
            ) {
                // this.query = 'closeOnClickOutside 1';

                // this.open();//this creates erratic behaviour and isn't really needed
            } else if (! this.isOnMobile) {
                // this.query = 'closeOnClickOutside 2';
                // this.close();
            } else {
                // this.query = 'closeOnClickOutside 3';
            }
        },

        open() {
            this.isOpen = true;
        },

        close() {
            this.isOpen = false;
        },

        //destination (country, province/region, place)

        initSelectedFilters() {
            this.selected['country'] = this.getSelectedFromFilterData(this.filterData['country']);
            this.selected['province'] = this.getSelectedFromFilterData(this.filterData['province-region'], 'province');
            this.selected['region'] = this.getSelectedFromFilterData(this.filterData['province-region'], 'region');
            this.selected['place'] = this.getSelectedFromFilterData(this.filterData['place']);
        },

        getSelectedFromFilterData(filterData, targetKey = null) {
            const filtersToCheck = filterData?.selectedFilters ?? {};

            let selected = [];

            Object.entries(filtersToCheck).forEach(([key, filterToCheck]) => {
                if (targetKey === null || key === targetKey) {
                    if (Object.values(filterToCheck).length > 0) {
                        selected = selected.concat(Object.keys(filterToCheck));
                    }
                }
            });

            return selected;
        },

        clearFilterData() {
            this.filterCategory = '';
            this.filterReference = '';
            this.filters = [];
        },

        clearSelected() {
            this.selected = {
                'country': [],
                'province': [],
                'region': [],
                'place': [],
                'geo-location': [],
            };
        },

        //this is an event because we can have multiple instances of this component
        clearAllFilters() {
            this.$nextTick(() => {
                this.Bus.emit('clear-input-suggest');
            });

            this.hasUpdatedFilters = true;
        },

        clearFilters() {

            this.query = '';
            this.previousQuery = '';

            const hasLocationFilter = this.filterCategory === '';

            this.clearFilterData();
            this.clearSelected();
            this.filters = [];

            this.Bus.emit('open-destination-sub-filters');

            if (this.allowFilters) {
                this.$nextTick(() => {

                    //this is getting out of hand
                    const cancels = hasLocationFilter
                        ? Object.values(this.filterData.country.cancels)[0]
                        : Object.values(this.filterData['geo-location'].cancels)[0]
                    ;

                    this.Bus.emit('filter-change', cancels);
                })
            }
        },

        hasNoValidResults() {
            return this.filteredResults?.filter(option => option.source === 'X').length > 0;
        },

        handleMapLinkClick() {

            if (this.context === 'homepage') {
                //les hackerables
                window.location.href = document.querySelector('.primary-search-button').getAttribute('href') + '#mapView';
            } else {
                this.Bus.emit('open-maps-popup');
            }
        },
    },

    computed: {

        isOnMobile() {
            return window.innerWidth <= 1024;
        },

        noResultsText() {
            return this.filterData['geo-location']?.translations.noResults;
        },

        noResultsTextShort() {
            return this.filterData['geo-location']?.translations.noResultsShort;
        },

        placeholder() {
            return this.filterData['geo-location']?.translations.placeholder;
        },

        selectedOption() {
            return 'option' + this.selectedIndex;
        },

    }
};
</script>
